import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import cn from "classnames";
import { TEMPLATES } from "@hicya/habits-shared/data";
import { NumberStepper, Section } from "@hicya/ui";

import { defaultHabitColor } from "@/components/ui/habit-icon-map";
import { CategoryPicker } from "@/components/form/category-picker";
import { ColorPicker } from "@/components/form/color-picker";
import { IconPicker } from "@/components/form/icon-picker";
import { ScheduleConfig } from "@/components/form/schedule-config";
import { useHabits } from "@/context/habits-context";
import { useDatabase } from "@/db/provider";
import { trackHabitCreated } from "@/lib/analytics";

import styles from "./habit-create.module.scss";

import type { HabitType, ScheduleType, TimeOfDay } from "@hicya/habits-shared/db";

const TIME_OPTIONS: Array<{ value: TimeOfDay; label: string }> = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "anytime", label: "Anytime" },
];

function buildScheduleValue(
  type: ScheduleType,
  days: number[],
  count: number,
  interval: number,
): string {
  switch (type) {
    case "daily":
      return "{}";
    case "specific_days":
      return JSON.stringify({ days });
    case "x_per_week":
    case "x_per_month":
      return JSON.stringify({ count });
    case "every_n_days":
      return JSON.stringify({ interval });
  }
}

export function HabitCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, createHabit, createCategory } = useHabits();
  const db = useDatabase();

  // Form state
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("\u2764\uFE0F");
  const [color, setColor] = useState("#86b5c4");
  const [habitType, setHabitType] = useState<HabitType>("boolean");
  const [targetCount, setTargetCount] = useState(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>("daily");
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [count, setCount] = useState(3);
  const [interval, setInterval] = useState(2);
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>("anytime");
  const [showStreak, setShowStreak] = useState(true);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [habitCount, setHabitCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Load habit count for warning
  useEffect(() => {
    void db.getHabitCount().then((c) => setHabitCount(Number(c)));
  }, [db]);

  // Apply template if present
  useEffect(() => {
    const templateId = searchParams.get("template");
    if (templateId) {
      const template = TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setName(template.name);
        setIcon(template.icon);
        setColor(defaultHabitColor(template.icon));
        setHabitType(template.type);
        setScheduleType(template.scheduleType);
        setTargetCount(template.targetCount);
        setTimeOfDay(template.timeOfDay);
        if (template.scheduleType === "specific_days") {
          try {
            const parsed = JSON.parse(template.scheduleValue) as { days?: number[] };
            if (parsed.days) setDays(parsed.days);
          } catch {
            /* use defaults */
          }
        }
        if (template.scheduleType === "x_per_week" || template.scheduleType === "x_per_month") {
          try {
            const parsed = JSON.parse(template.scheduleValue) as { count?: number };
            if (parsed.count) setCount(parsed.count);
          } catch {
            /* use defaults */
          }
        }
        if (template.scheduleType === "every_n_days") {
          try {
            const parsed = JSON.parse(template.scheduleValue) as { interval?: number };
            if (parsed.interval) setInterval(parsed.interval);
          } catch {
            /* use defaults */
          }
        }
      }
    }
  }, [searchParams]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);

    try {
      const scheduleValue = buildScheduleValue(scheduleType, days, count, interval);
      const sortOrder = habitCount;

      await createHabit({
        id: crypto.randomUUID(),
        name: name.trim(),
        type: habitType,
        schedule_type: scheduleType,
        schedule_value: scheduleValue,
        time_of_day: timeOfDay,
        target_count: habitType === "count" ? targetCount : 1,
        color,
        icon,
        category_id: categoryId,
        sort_order: sortOrder,
        day_boundary_hour: 0,
        show_streak: showStreak,
        archived_at: null,
        deleted_at: null,
      });

      trackHabitCreated(!!searchParams.get("template"));
      void navigate("/today");
    } finally {
      setSubmitting(false);
    }
  }, [
    name,
    submitting,
    scheduleType,
    days,
    count,
    interval,
    habitCount,
    categoryId,
    createHabit,
    habitType,
    targetCount,
    color,
    icon,
    timeOfDay,
    showStreak,
    navigate,
    searchParams,
  ]);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <button type="button" className={styles.back} onClick={() => navigate(-1)}>
          <ArrowLeftIcon size={20} aria-hidden="true" /> Back
        </button>
        <button
          type="button"
          className={styles.templates}
          onClick={() => navigate("/habits/new/templates")}
        >
          Browse templates
        </button>
      </div>

      <h2 className={styles.heading}>New Habit</h2>

      <div className={styles.form}>
        <Section title="Basics">
          <input
            type="text"
            className={styles.input}
            placeholder="Habit name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
            aria-label="Habit name"
            data-testid="habit-name-input"
          />
          <IconPicker value={icon} onChange={setIcon} />
          <ColorPicker value={color} onChange={setColor} />
        </Section>

        {state.categories.length > 0 && (
          <Section title="Category">
            <CategoryPicker
              categories={state.categories}
              value={categoryId}
              onChange={setCategoryId}
              onCreateCategory={(cat) => void createCategory(cat)}
            />
          </Section>
        )}

        <Section title="Type & Target">
          <div className={styles.typeToggle} role="radiogroup" aria-label="Habit type">
            <button
              type="button"
              className={cn(styles.typeOption, habitType === "boolean" && styles.active)}
              onClick={() => setHabitType("boolean")}
              role="radio"
              aria-checked={habitType === "boolean"}
            >
              Yes/No
            </button>
            <button
              type="button"
              className={cn(styles.typeOption, habitType === "count" && styles.active)}
              onClick={() => setHabitType("count")}
              role="radio"
              aria-checked={habitType === "count"}
            >
              Count
            </button>
          </div>
          {habitType === "count" && (
            <NumberStepper
              value={targetCount}
              onChange={setTargetCount}
              min={1}
              max={100}
              label="Target count"
            />
          )}
        </Section>

        <Section title="Schedule">
          <ScheduleConfig
            scheduleType={scheduleType}
            onScheduleTypeChange={setScheduleType}
            days={days}
            onDaysChange={setDays}
            count={count}
            onCountChange={setCount}
            interval={interval}
            onIntervalChange={setInterval}
          />
        </Section>

        <Section title="Time of Day">
          <div className={styles.timeOptions} role="radiogroup" aria-label="Time of day">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(styles.timeCard, timeOfDay === opt.value && styles.active)}
                onClick={() => setTimeOfDay(opt.value)}
                role="radio"
                aria-checked={timeOfDay === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Section>

        <Section title="Advanced" collapsible defaultExpanded={false}>
          <label className={styles.toggleRow}>
            <span>Show streak</span>
            <input
              type="checkbox"
              checked={showStreak}
              onChange={(e) => setShowStreak(e.target.checked)}
            />
          </label>
        </Section>
      </div>

      {habitCount >= 26 && (
        <p className={styles.warning}>
          You&apos;re tracking {habitCount} habits — research shows focusing on fewer leads to
          better consistency. Consider archiving habits you&apos;ve already built.
        </p>
      )}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.submit}
          onClick={() => void handleSubmit()}
          disabled={!name.trim() || submitting}
          data-testid="create-habit-button"
        >
          {submitting ? "Creating..." : "Create Habit"}
        </button>
      </div>
    </div>
  );
}
